import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormRenderer, getTheme, getThemeStyle, insertWebFont } from '@heyform-inc/form-renderer';
import type { FormRendererProps } from '@heyform-inc/form-renderer';
import type { HiddenFieldAnswer } from '@heyform-inc/shared-types-enums';
import { Alert } from './Alert';
import { Toaster, useToasts } from './Toast';
import {
  pollSubmissionStatus,
  precheckSubmission,
  resolveIdentification,
  validateDateOfBirth,
  type HiddenFieldValue,
} from '../lib/submissionFeedback';

// IFormModel isn't re-exported from form-renderer's index — derive from props
type IFormModel = FormRendererProps['form'];
interface FormHiddenField {
  id: string;
  name: string;
}

// ---------------------------------------------------------------------------
// Clinic / facility reference resolution
//
// The downstream service that consumes submissions REQUIRES the "clinicref"
// hidden field; a submission without it is rejected with a "missing-location"
// error. We resolve that one field by a normalized name so casing/separator
// variants (e.g. "Clinic-Ref", "clinic_ref", "CLINICREF") still match, but we
// deliberately match ONLY "clinicref" — no other field receives the value.
// ---------------------------------------------------------------------------

const CLINIC_REF_FIELD_NAME = 'clinicref';

const normalizeName = (name: string): string =>
  name.toLowerCase().replace(/[^a-z0-9]/g, '');

function findClinicRefField(
  form: IFormModel | null,
): FormHiddenField | undefined {
  return ((form?.hiddenFields ?? []) as FormHiddenField[]).find(
    field => normalizeName(field.name) === CLINIC_REF_FIELD_NAME,
  );
}

// ---------------------------------------------------------------------------
// GraphQL helpers — plain fetch, no Apollo needed
// ---------------------------------------------------------------------------

const GQL_ENDPOINT = '/graphql';

// Generate a stable anonymous session ID for this page load.
// HeyForm's EndpointAnonymousIdGuard requires x-anonymous-id on openForm.
const anonymousId = crypto.randomUUID();

async function gql<T>(
  query: string,
  variables: Record<string, unknown>,
): Promise<T> {
  const res = await fetch(GQL_ENDPOINT, {
    method: 'POST',
    // 'omit': don't forward the HeyForm admin session cookie — if the cookie
    // were forwarded the CSRF guard would reject the request as "Forbidden".
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/json',
      'x-anonymous-id': anonymousId,
    },
    body: JSON.stringify({ query, variables }),
  });

  const json = (await res.json()) as { data?: Record<string, T>; errors?: { message: string }[] };

  if (json.errors?.length) {
    throw new Error(json.errors[0].message);
  }

  // Return the first data key value
  const dataKey = Object.keys(json.data ?? {})[0];
  return (json.data as Record<string, T>)[dataKey];
}

// ---------------------------------------------------------------------------
// GraphQL documents (mirrors the webapp's graphql.ts)
// ---------------------------------------------------------------------------

const PUBLIC_FORM_GQL = `
  query publicForm($input: FormDetailInput!) {
    publicForm(input: $input) {
      id
      teamId
      memberId
      name
      description
      interactiveMode
      kind
      stripeAccount { accountId email }
      settings {
        captchaKind active enableExpirationDate expirationTimeZone
        enabledAt closedAt enableTimeLimit timeLimit filterSpam
        published allowArchive password requirePassword
        enableQuotaLimit quotaLimit enableIpLimit ipLimitCount ipLimitTime
        enableProgress enableQuestionList locale languages
        enableClosedMessage closedFormTitle closedFormDescription
      }
      fields {
        id title titleSchema description kind
        validations properties layout
      }
      translations
      hiddenFields { id name }
      logics
      variables
      fieldUpdateAt
      themeSettings { theme }
      retentionAt suspended draft status integrations
    }
  }
`;

const OPEN_FORM_GQL = `
  query openForm($input: OpenFormInput!) {
    openForm(input: $input)
  }
`;

const COMPLETE_SUBMISSION_GQL = `
  mutation completeSubmission($input: CompleteSubmissionInput!) {
    completeSubmission(input: $input) {
      clientSecret
    }
  }
`;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface HeyFormEmbedProps {
  formId: string;
  clinicRef: string;
  /** Logged-in submitter, carried downstream so errors can be routed back. */
  submitterEmail?: string;
  submitterId?: string;
  /** Returns a fresh Keycloak bearer token for the mediator calls. Omitted on the
   *  web-component embed path (no Keycloak) → calls go out without Authorization. */
  getToken?: () => Promise<string | undefined>;
  /** Increment to reset the form to a blank state */
  resetKey?: number;
}

// Mediator base URL for Option A (validate) + Option B (status polling).
// Empty => feature OFF (submission behaves exactly as before).
const mediatorBaseUrl = window.__APP_CONFIG__.mediatorBaseUrl;

// In a "test" environment only the dedicated test form (formKeyTest) exchanges
// data with the mediator; every other form behaves as if the feature were off.
// "prod" (or unset) enables it for all forms.
const appEnv = window.__APP_CONFIG__.env;
const formKeyTest = window.__APP_CONFIG__.formKeyTest;

const HeyFormEmbed: React.FC<HeyFormEmbedProps> = ({
  formId,
  clinicRef,
  submitterEmail,
  submitterId,
  getToken,
  resetKey,
}) => {
  const [form, setForm] = useState<IFormModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const openTokenRef = useRef<string | undefined>(undefined);

  // Toasts for async (Option B) outcomes.
  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();
  // In-flight status polls. Polls run independently and intentionally survive a
  // form reset ("New patient") so a result for an earlier submission can still
  // surface — they are only cancelled on unmount. Each poll prunes itself via
  // onSettled when it ends on its own.
  const cancelPollsRef = useRef<Set<() => void>>(new Set());
  useEffect(
    () => () => {
      cancelPollsRef.current.forEach(cancel => cancel());
      cancelPollsRef.current.clear();
    },
    [],
  );
  // Tracks whether the component is still mounted, so an async submit that
  // resolves after unmount doesn't start an orphan poll / toast.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Re-fetch whenever formId or resetKey changes
  useEffect(() => {
    let cancelled = false;

    async function loadForm() {
      setLoading(true);
      setError(null);
      openTokenRef.current = undefined;

      try {
        const [formData, openToken] = await Promise.all([
          gql<IFormModel>(PUBLIC_FORM_GQL, { input: { formId } }),
          gql<string>(OPEN_FORM_GQL, { input: { formId } }),
        ]);

        if (cancelled) return;
        setForm(formData);
        openTokenRef.current = openToken;
      } catch (err: unknown) {
        if (cancelled) return;
        setError(
          err instanceof Error ? err.message : 'Failed to load form',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadForm();
    return () => { cancelled = true; };
  }, [formId, resetKey]);

  // Compute theme CSS variables — must be before any early return (Rules of Hooks).
  // getThemeStyle() returns a CSS string that sets --heyform-* vars on `html`.
  // FormRenderer relies on them but does NOT inject them — the host app must.
  const themeStyle = useMemo(() => {
    if (!form) return '';
    const theme = getTheme(form.themeSettings?.theme);
    insertWebFont(theme.fontFamily);
    return getThemeStyle(theme, { clinicref: clinicRef });
  }, [form, clinicRef]);

  // The hidden field that will carry the clinic/facility reference, resolved
  // by normalized name. undefined => the form is misconfigured.
  const clinicRefField = useMemo(() => findClinicRefField(form), [form]);

  // Whether this form may exchange data with the mediator (precheck + DOB guard
  // + status polling). In a test environment that's the test form only.
  const feedbackEnabled = appEnv === 'test' ? formId === formKeyTest : true;

  const handleSubmit = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (values: Record<string, any>) => {
      if (!form) return;

      // Hard guard — NEVER submit without the clinic/facility reference.
      // The thrown message is surfaced by FormRenderer as a submit error and
      // no request reaches the server, so a submission can't be created
      // without a reference (which the downstream service requires).
      if (!clinicRef) {
        throw new Error(
          'Cannot submit: no clinic/facility reference is available for your session. ' +
            'Please sign in again.',
        );
      }
      if (!clinicRefField) {
        throw new Error(
          'Cannot submit: this form is not configured with a clinic/facility ' +
            'reference hidden field (expected one named "clinicref"). Please contact support.',
        );
      }

      // Correlation id for this attempt — minted here, carried as a hidden
      // field, and used to poll the mediator for the async outcome (Option B).
      const requestId = crypto.randomUUID();

      // clinicref uses the form's declared field (id+name preserved). Identity
      // and correlation fields are pass-through metadata the mediator reads by
      // name (id == name), and don't need to be declared on the form.
      const metadata: Record<string, string | undefined> = {
        requestId,
        submitterEmail,
        submitterId,
      };
      const hiddenFields: HiddenFieldValue[] = [
        { id: clinicRefField.id, name: clinicRefField.name, value: clinicRef },
        ...Object.entries(metadata)
          .filter((entry): entry is [string, string] => Boolean(entry[1]))
          .map(([name, value]) => ({ id: name, name, value })),
      ];

      // Submission feedback (Option A precheck + Option B polling) is gated per
      // form: in a test environment only the dedicated test form talks to the
      // mediator. When disabled, this form submits exactly as it did before the
      // feature existed.
      let token: string | undefined;
      if (feedbackEnabled) {
        // Basic client-side DOB sanity check — block ONLY on an unambiguously
        // wrong date (future / unparseable). The thrown message is shown by
        // FormRenderer and no submission is created, so the user can correct it.
        const dobError = validateDateOfBirth(form.fields as never[], values);
        if (dobError) throw new Error(dobError);

        // Fresh Keycloak bearer token for the mediator calls (undefined on the
        // web-component path, which has no Keycloak).
        token = getToken ? await getToken() : undefined;

        // Option A — advisory duplicate-ID precheck. NON-BLOCKING: if the ID is
        // already registered we show an info notice and submit anyway (the
        // Stage-2 duplicate is suppressed for the user, so this is the only place
        // they learn of it). Everything else (no id, network, 5xx, timeout) fails
        // open inside precheckSubmission, so a flaky/not-yet-deployed HIE is a no-op.
        const precheck = await precheckSubmission(
          mediatorBaseUrl,
          resolveIdentification(form.fields as never[], values),
          token,
        );
        if (precheck.duplicate) {
          pushToast(
            'info',
            precheck.message ?? 'This ID is already registered. Submitting anyway.',
            8000,
          );
        }
      }

      await gql(COMPLETE_SUBMISSION_GQL, {
        input: {
          formId,
          openToken: openTokenRef.current,
          answers: values,
          hiddenFields: hiddenFields as HiddenFieldAnswer[],
        },
      });

      // If the component unmounted during the awaited submit above, don't start
      // an orphan poll — nothing would cancel it and it would toast post-unmount.
      if (!mountedRef.current) return;
      if (!feedbackEnabled) return;

      // Option B — submission accepted; poll for the async (Stage-1/Stage-2)
      // outcome and toast it if it arrives while the page is open. Fire-and-
      // forget: the submit itself already succeeded.
      const polls = cancelPollsRef.current;
      const cancel = pollSubmissionStatus(mediatorBaseUrl, requestId, {
        onResult: status => {
          if (status.status === 'error') {
            pushToast(
              'error',
              status.fixable && status.message
                ? status.message
                : 'Your submission was saved, but registration needs review. No action is needed from you.',
            );
          } else {
            pushToast('success', 'Submission processed successfully.', 6000);
          }
        },
        onSettled: () => polls.delete(cancel),
        token,
      });
      polls.add(cancel);
    },
    [form, formId, clinicRef, clinicRefField, feedbackEnabled, submitterEmail, submitterId, getToken, pushToast],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-10 h-10 rounded-full border-4 border-gray-200 border-t-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  if (!form) return null;

  // Block the form from even mounting (so it can't be filled or submitted)
  // unless a clinic/facility reference is available AND the form has a hidden
  // field to carry it. This is the primary safeguard; handleSubmit's throw is
  // defense-in-depth.
  if (!clinicRef) {
    return (
      <div className="flex items-center justify-center h-full">
        <Alert variant="error">
          No clinic/facility reference is available for your session, so this
          form can&apos;t be submitted. Please sign in again.
        </Alert>
      </div>
    );
  }

  if (!clinicRefField) {
    return (
      <div className="flex items-center justify-center h-full">
        <Alert variant="error">
          This form is not configured with a clinic/facility reference field, so
          submissions can&apos;t be linked to a clinic. Please contact support.
        </Alert>
      </div>
    );
  }

  return (
    <div className="w-full h-full">
      {/* Inject form theme CSS variables into the document */}
      {themeStyle && <style dangerouslySetInnerHTML={{ __html: themeStyle }} />}
      <FormRenderer
        form={form}
        locale={form.settings?.locale ?? 'en'}
        query={{ clinicref: clinicRef }}
        alwaysShowNextButton
        onSubmit={handleSubmit}
      />
      <Toaster toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default HeyFormEmbed;
